import { useState, useEffect, useCallback } from 'react';
import { getValidToken } from '../utils/auth';
import {
  AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || '';

const PERIODS = [
  { value: '1h', label: '1 Hour' },
  { value: '6h', label: '6 Hours' },
  { value: '24h', label: '24 Hours' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
];

function formatBytes(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return bytes.toFixed(0) + ' B/s';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB/s';
  if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB/s';
  return (bytes / 1073741824).toFixed(2) + ' GB/s';
}

function formatTime(iso, period) {
  const d = new Date(iso);
  if (period === '1h' || period === '6h') return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (period === '24h') return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (period === '7d') return d.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="h-56">
        {children}
      </div>
    </div>
  );
}

export default function ServerMonitor() {
  const [period, setPeriod] = useState('1h');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${API_URL}/api/admin/monitor/stats?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch stats');
      const json = await res.json();
      setData(json || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    setLoading(true);
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const chartData = data.map((p) => ({
    ...p,
    label: formatTime(p.time, period),
  }));

  // Get latest values for subtitles
  const latest = data.length > 0 ? data[data.length - 1] : {};

  if (error && data.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Server Monitor</h1>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Server Monitor</h1>
        <div className="flex items-center gap-3">
          {loading && data.length > 0 && (
            <div className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          )}
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && data.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
          Loading metrics...
        </div>
      ) : data.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
          No data yet. Metrics are collected every 30 seconds.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* CPU Usage */}
          <ChartCard
            title="CPU Usage"
            subtitle={latest.cpu_percent != null ? `Current: ${latest.cpu_percent}%` : null}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v) => [`${v}%`, 'CPU']} />
                <Area type="monotone" dataKey="cpu_percent" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.15} strokeWidth={1.5} dot={false} name="CPU %" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Memory Usage */}
          <ChartCard
            title="Memory Usage"
            subtitle={latest.memory_used_mb != null ? `${latest.memory_used_mb} / ${latest.memory_total_mb} MB (${latest.memory_percent}%)` : null}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v) => [`${v}%`, 'Memory']} />
                <Area type="monotone" dataKey="memory_percent" stroke="#2563eb" fill="#2563eb" fillOpacity={0.15} strokeWidth={1.5} dot={false} name="Memory %" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Disk Usage */}
          <ChartCard
            title="Disk Usage"
            subtitle={latest.disk_used_gb != null ? `${latest.disk_used_gb} / ${latest.disk_total_gb} GB (${latest.disk_percent}%)` : null}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v) => [`${v}%`, 'Disk']} />
                <Area type="monotone" dataKey="disk_percent" stroke="#d97706" fill="#d97706" fillOpacity={0.15} strokeWidth={1.5} dot={false} name="Disk %" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Disk I/O */}
          <ChartCard
            title="Disk I/O"
            subtitle={latest.disk_read_rate != null ? `Read: ${formatBytes(latest.disk_read_rate)} | Write: ${formatBytes(latest.disk_write_rate)}` : null}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatBytes(v).replace('/s', '')} />
                <Tooltip formatter={(v) => [formatBytes(v)]} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="disk_read_rate" stroke="#16a34a" strokeWidth={1.5} dot={false} name="Read" />
                <Line type="monotone" dataKey="disk_write_rate" stroke="#dc2626" strokeWidth={1.5} dot={false} name="Write" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Bandwidth */}
          <ChartCard
            title="Network Bandwidth"
            subtitle={latest.net_rx_rate != null ? `In: ${formatBytes(latest.net_rx_rate)} | Out: ${formatBytes(latest.net_tx_rate)}` : null}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatBytes(v).replace('/s', '')} />
                <Tooltip formatter={(v) => [formatBytes(v)]} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="net_rx_rate" stroke="#0891b2" strokeWidth={1.5} dot={false} name="Inbound" />
                <Line type="monotone" dataKey="net_tx_rate" stroke="#ea580c" strokeWidth={1.5} dot={false} name="Outbound" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Load Average */}
          <ChartCard
            title="Load Average"
            subtitle={latest.load_1m != null ? `1m: ${latest.load_1m} | 5m: ${latest.load_5m} | 15m: ${latest.load_15m}` : null}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend iconSize={10} wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="load_1m" stroke="#7c3aed" strokeWidth={1.5} dot={false} name="1 min" />
                <Line type="monotone" dataKey="load_5m" stroke="#2563eb" strokeWidth={1.5} dot={false} name="5 min" />
                <Line type="monotone" dataKey="load_15m" stroke="#6b7280" strokeWidth={1.5} dot={false} name="15 min" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}
    </div>
  );
}
